<script setup lang="ts">
  import { computed } from 'vue';

  import IconImage from '@/shared/ui/common/IconImage.vue';
  import UpAndDownIcon from '@/shared/assets/icons/UpAndDown.svg';

  const props = defineProps<{
    modelValue: number | null;
    inputId?: string;
    min?: number;
    max?: number;
    step?: number;
    placeholder?: string;
    disabled?: boolean;
    readonly?: boolean;
    ariaLabel?: string;
    ariaLabelledby?: string;
    inputClass?: string;
    wrapperClass?: string;
    nullOnDecrementBelowMin?: boolean;
  }>();

  const emit = defineEmits<{
    (e: 'update:modelValue', value: number | null): void;
  }>();

  const UPDATE_MODEL_VALUE_EVENT = 'update:modelValue';

  const stepValue = computed(() => props.step ?? 1);

  const clampValue = (value: number): number => {
    let nextValue = value;
    if (props.min !== undefined && nextValue < props.min) {
      nextValue = props.min;
    }
    if (props.max !== undefined && nextValue > props.max) {
      nextValue = props.max;
    }
    return nextValue;
  };

  const inputValue = computed({
    get: () => props.modelValue ?? '',
    set: (value: string | number) => {
      if (value === '' || value === null || value === undefined) {
        emit(UPDATE_MODEL_VALUE_EVENT, null);
        return;
      }
      const numericValue = typeof value === 'string' ? Number(value) : value;
      if (Number.isNaN(numericValue)) {
        emit(UPDATE_MODEL_VALUE_EVENT, null);
        return;
      }
      emit(UPDATE_MODEL_VALUE_EVENT, clampValue(numericValue));
    },
  });

  const handleIncrement = () => {
    if (props.disabled || props.readonly) return;
    if (props.modelValue === null) {
      const initialValue = props.min ?? stepValue.value;
      emit(UPDATE_MODEL_VALUE_EVENT, clampValue(initialValue));
      return;
    }
    const nextValue = clampValue(props.modelValue + stepValue.value);
    emit(UPDATE_MODEL_VALUE_EVENT, nextValue);
  };

  const handleDecrement = () => {
    if (props.disabled || props.readonly) return;
    if (props.modelValue === null) {
      return;
    }
    const nextValue = props.modelValue - stepValue.value;
    if (props.min !== undefined && nextValue < props.min) {
      if (props.nullOnDecrementBelowMin) {
        emit(UPDATE_MODEL_VALUE_EVENT, null);
        return;
      }
      emit(UPDATE_MODEL_VALUE_EVENT, props.min);
      return;
    }
    emit(UPDATE_MODEL_VALUE_EVENT, clampValue(nextValue));
  };
</script>

<template>
  <div :class="['relative inline-block', wrapperClass]">
    <input
      :id="inputId"
      v-model="inputValue"
      type="number"
      :min="min"
      :max="max"
      :step="stepValue"
      :placeholder="placeholder"
      :disabled="disabled"
      :readonly="readonly"
      :aria-label="ariaLabel"
      :aria-labelledby="ariaLabelledby"
      :class="[
        'w-22 px-4 py-2.5 pr-9 rounded-lg border border-gray-02 text-gray-03 text-sm/5.5 text-left [appearance:textfield] [-webkit-appearance:textfield] [-moz-appearance:textfield] [&::-webkit-outer-spin-button]:m-0 [&::-webkit-inner-spin-button]:m-0 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none',
        inputClass,
      ]"
    >
    <div class="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
      <IconImage
        :src="UpAndDownIcon"
        alt="Aumentar ou diminuir"
        class="h-4 w-3"
      />
    </div>
    <div class="absolute right-3 top-1/2 -translate-y-1/2 w-3 h-4 pointer-events-auto">
      <div
        class="absolute top-0 left-0 w-full h-1/2 cursor-pointer"
        title="Aumentar"
        @mousedown.prevent
        @click.stop="handleIncrement"
      />
      <div
        class="absolute bottom-0 left-0 w-full h-1/2 cursor-pointer"
        title="Diminuir"
        @mousedown.prevent
        @click.stop="handleDecrement"
      />
    </div>
  </div>
</template>
